// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Collection storage mode configuration.
 *
 * Determines whether each collection is served from local RxDB (IndexedDB)
 * or remote D1 REST API. Community tier routes 19 collections to remote;
 * premium tier keeps all 32 local.
 */

export type CollectionMode = 'local' | 'remote'

export interface CollectionModeConfig {
  /** Base URL for D1 REST API (remote collections) */
  apiBaseUrl: string
  /** Auth token getter for remote requests */
  getAuthToken: () => Promise<string>
  /** Default mode when collection not explicitly listed */
  defaultMode: CollectionMode
  /** Per-collection mode overrides */
  collections: Record<string, CollectionMode>
  /** react-query defaults for remote collections */
  remoteDefaults?: {
    staleTime?: number // default: 30_000 (30s)
    refetchInterval?: number // default: false (manual)
    gcTime?: number // default: 300_000 (5min)
  }
}

/**
 * Resolve the storage mode for a given collection.
 *
 * Checks explicit per-collection overrides first, then falls back to defaultMode.
 */
export function getCollectionMode(
  config: CollectionModeConfig,
  name: string,
): CollectionMode {
  return config.collections[name] ?? config.defaultMode
}
